import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { API_BASE } from '../config/api';

export const ScholarshipListing = () => {
  const navigate = useNavigate();
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedAvailability, setSelectedAvailability] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileDisplayCount, setMobileDisplayCount] = useState(5);
  const [isFilterMinimized, setIsFilterMinimized] = useState(false);
  const itemsPerPage = 16; // 4 columns x 4 rows
  
  const backendUrl = API_BASE;

  // Scroll behavior for sticky filter
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setIsFilterMinimized(true);
      } else {
        setIsFilterMinimized(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchScholarships = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${backendUrl}/api/public/scholarships`);
        if (!response.ok) throw new Error('Failed to fetch scholarships');
        const data = await response.json();
        setScholarships(data.scholarships || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchScholarships();
  }, [backendUrl]);

  // Filter scholarships
  const filteredScholarships = scholarships.filter(scholarship => {
    if (selectedLevel && scholarship.eligible_levels && !scholarship.eligible_levels.includes(selectedLevel)) return false;
    if (selectedField && scholarship.field_of_study && scholarship.field_of_study !== selectedField) return false;
    if (selectedUniversity && scholarship.university_id && scholarship.university_id !== selectedUniversity) return false;
    if (selectedType && scholarship.type && scholarship.type !== selectedType) return false;
    if (selectedAvailability === 'open' && scholarship.deadline && new Date(scholarship.deadline) < new Date()) return false;
    return true;
  });

  // Featured scholarships (first 3 per filter)
  const hasActiveFilters = selectedLevel || selectedField || selectedUniversity || selectedType || selectedAvailability;
  const featuredScholarships = hasActiveFilters ? filteredScholarships.slice(0, 3) : [];
  
  // Pagination
  const totalPages = Math.ceil(filteredScholarships.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedScholarships = filteredScholarships.slice(startIndex, endIndex);
  
  // Mobile progressive load
  const mobileScholarships = filteredScholarships.slice(0, mobileDisplayCount);

  const handleClearFilters = () => {
    setSelectedLevel('');
    setSelectedField('');
    setSelectedUniversity('');
    setSelectedType('');
    setSelectedAvailability('');
    setCurrentPage(1);
    setMobileDisplayCount(5);
  };

  const handleConsultClick = () => {
    window.open('https://wa.me/60123456789?text=Hi, I would like to inquire about scholarships', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-[#4B5563]">Loading scholarships...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111827] mb-4">Unable to Load Scholarships</h1>
            <p className="text-[#4B5563] mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <Header />
      
      <main className="flex-1 w-full max-w-full px-4 md:px-12 lg:px-20">
        {/* PAGE HEADER */}
        <section className="py-10 md:py-12 bg-white rounded-2xl mt-6 shadow-sm w-full max-w-full">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#111827] mb-4">
              Find Scholarships to Fund Your Education
            </h1>
            <p className="text-lg text-[#6B7280] max-w-3xl mx-auto">
              Explore financial aid opportunities from universities and organizations to support your academic journey
            </p>
          </div>
        </section>

        {/* FILTER BAR - Minimized Sticky */}
        {isFilterMinimized && (
          <section className="fixed top-16 left-0 right-0 z-30 bg-white border-b-2 border-[#E5E7EB] shadow-md transition-all duration-300">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-sm font-semibold text-[#111827]">Filters:</span>
                  <div className="flex flex-wrap gap-2">
                    {selectedLevel && <span className="px-2 py-1 bg-[#E0F2FE] text-[#0369A1] text-xs rounded">{selectedLevel}</span>}
                    {selectedField && <span className="px-2 py-1 bg-[#F3E8FF] text-[#7C3AED] text-xs rounded">{selectedField}</span>}
                    {selectedType && <span className="px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs rounded">{selectedType}</span>}
                    {selectedAvailability && <span className="px-2 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs rounded">{selectedAvailability}</span>}
                  </div>
                </div>
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-[#F97316] hover:text-[#fb7e2e] font-semibold whitespace-nowrap"
                >
                  Clear All
                </button>
              </div>
            </div>
          </section>
        )}

        {/* FILTER BAR - Full View */}
        {!isFilterMinimized && (
        <section className="py-6 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#111827]">Filter Scholarships</h2>
              <button
                onClick={handleClearFilters}
                className="text-sm text-[#F97316] hover:text-[#fb7e2e] font-semibold flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Clear All
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <select
                value={selectedLevel}
                onChange={(e) => { setSelectedLevel(e.target.value); setCurrentPage(1); }}
                className="px-4 py-2 border-2 border-[#E5E7EB] rounded-lg text-sm focus:border-[#1E40AF] focus:outline-none"
              >
                <option value="">Level of Study</option>
                <option value="Bachelor">Bachelor</option>
                <option value="Master">Master</option>
                <option value="PhD">PhD</option>
                <option value="Diploma">Diploma</option>
              </select>
              
              <select
                value={selectedField}
                onChange={(e) => { setSelectedField(e.target.value); setCurrentPage(1); }}
                className="px-4 py-2 border-2 border-[#E5E7EB] rounded-lg text-sm focus:border-[#1E40AF] focus:outline-none"
              >
                <option value="">Field of Study</option>
                <option value="Engineering">Engineering</option>
                <option value="Business">Business</option>
                <option value="Computer Science">Computer Science</option>
                <option value="Medicine">Medicine</option>
                <option value="Arts">Arts</option>
              </select>
              
              <select
                value={selectedType}
                onChange={(e) => { setSelectedType(e.target.value); setCurrentPage(1); }}
                className="px-4 py-2 border-2 border-[#E5E7EB] rounded-lg text-sm focus:border-[#1E40AF] focus:outline-none"
              >
                <option value="">Scholarship Type</option>
                <option value="Merit">Merit</option>
                <option value="Entry">Entry</option>
                <option value="Need-Based">Need-Based</option>
                <option value="Early Bird">Early Bird</option>
                <option value="Internal">Internal</option>
              </select>
              
              <select
                value={selectedAvailability}
                onChange={(e) => { setSelectedAvailability(e.target.value); setCurrentPage(1); }}
                className="px-4 py-2 border-2 border-[#E5E7EB] rounded-lg text-sm focus:border-[#1E40AF] focus:outline-none"
              >
                <option value="">Availability</option>
                <option value="open">Open Now</option>
                <option value="all">All Year</option>
              </select>
              
              <div className="px-4 py-2 bg-[#F8FAFC] rounded-lg flex items-center justify-center">
                <span className="text-sm font-semibold text-[#6B7280]">
                  {filteredScholarships.length} Results
                </span>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* AD BANNER SLOT 1 */}
        <section className="py-6 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10">
            <div className="bg-gradient-to-r from-[#F8FAFC] to-[#EFF6FF] rounded-xl p-8 text-center border-2 border-[#E5E7EB]">
              <svg className="w-16 h-16 text-[#E5E7EB] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-[#9CA3AF]">Advertisement Banner Space</p>
            </div>
          </div>
        </section>

        {/* SCHOLARSHIP CARDS GRID */}
        <section className={`py-10 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full transition-all duration-300 ${isFilterMinimized ? 'pt-28 md:pt-32' : ''}`}>
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10">
            {filteredScholarships.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-20 h-20 text-[#E5E7EB] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-xl font-bold text-[#111827] mb-2">No Scholarships Found</h3>
                <p className="text-[#6B7280] mb-6">Try adjusting your filters or browse all available scholarships</p>
                <button
                  onClick={handleConsultClick}
                  className="px-6 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-xl text-white font-semibold rounded-lg transition-all"
                >
                  Consult Our Advisors
                </button>
              </div>
            ) : (
              <>
                {/* Top Pagination Info & Controls */}
                <div className="hidden md:flex items-center justify-between mb-6">
                  <p className="text-sm text-[#6B7280]">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredScholarships.length)} of {filteredScholarships.length} scholarships
                  </p>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="w-9 h-9 flex items-center justify-center bg-white border border-[#D1D5DB] rounded-lg text-sm text-[#6B7280] hover:bg-[#F9FAFB] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm text-[#6B7280]">Page {currentPage} of {totalPages}</span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="w-9 h-9 flex items-center justify-center bg-white border border-[#D1D5DB] rounded-lg text-sm text-[#6B7280] hover:bg-[#F9FAFB] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Desktop Grid - 4 columns */}
                <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {paginatedScholarships.map(scholarship => (
                    <ScholarshipCard
                      key={scholarship.id}
                      scholarship={scholarship}
                      isFeatured={featuredScholarships.includes(scholarship)}
                      navigate={navigate}
                    />
                  ))}
                </div>
                
                {/* Mobile Grid */}
                <div className="md:hidden grid grid-cols-1 gap-4">
                  {mobileScholarships.map(scholarship => (
                    <ScholarshipCard
                      key={scholarship.id}
                      scholarship={scholarship}
                      isFeatured={featuredScholarships.includes(scholarship)}
                      navigate={navigate}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* AD BANNER SLOT 2 */}
        {filteredScholarships.length > 0 && (
          <section className="py-6 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10">
              <div className="bg-gradient-to-r from-[#F8FAFC] to-[#EFF6FF] rounded-xl p-8 text-center border-2 border-[#E5E7EB]">
                <svg className="w-16 h-16 text-[#E5E7EB] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm text-[#9CA3AF]">Advertisement Banner Space</p>
              </div>
            </div>
          </section>
        )}

        {/* DESKTOP PAGINATION */}
        {filteredScholarships.length > 0 && totalPages > 1 && (
          <section className="py-6 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full hidden md:block">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10">
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center bg-white border border-[#D1D5DB] rounded-lg text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-[#111827] text-white shadow-md'
                          : 'bg-white border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF]'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 flex items-center justify-center bg-white border border-[#D1D5DB] rounded-lg text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </section>
        )}

        {/* MOBILE VIEW MORE */}
        {filteredScholarships.length > mobileDisplayCount && (
          <section className="py-6 bg-white rounded-2xl mt-4 mb-6 shadow-sm w-full max-w-full md:hidden">
            <div className="max-w-[1280px] mx-auto px-4 text-center">
              <button
                onClick={() => setMobileDisplayCount(prev => prev + 5)}
                className="px-8 py-3 bg-white border-2 border-[#1E40AF] text-[#1E40AF] hover:bg-[#1E40AF] hover:text-white font-semibold rounded-lg transition-all"
              >
                View More
              </button>
            </div>
          </section>
        )}
      </main>
      
      <Footer />
      <WhatsAppCTA phone="60123456789" message="Hi, I would like to inquire about scholarships" />
    </div>
  );
};

const ScholarshipCard = ({ scholarship, isFeatured, navigate }) => {
  return (
    <div
      onClick={() => navigate(`/scholarships/${scholarship.slug || scholarship.id}`)}
      className="bg-white border-2 border-[#E5E7EB] rounded-xl p-5 hover:border-[#F97316] hover:shadow-xl transition-all cursor-pointer flex flex-col h-full relative overflow-hidden"
    >
      {isFeatured && (
        <div className="absolute top-0 right-0">
          <div className="bg-gradient-to-br from-[#059669] to-[#047857] text-white font-bold px-2.5 py-1 rounded-bl-lg shadow-md text-xs">
            ⭐ Featured
          </div>
        </div>
      )}
      
      {/* Scholarship Name */}
      <h3 className="text-base font-bold text-[#111827] mb-2 line-clamp-3 pr-2 leading-tight" style={{ minHeight: '3.5rem' }}>
        {scholarship.name}
      </h3>
      
      {/* Provider */}
      <p className="text-xs text-[#6B7280] mb-3 font-medium">
        {scholarship.provider || 'University'}
      </p>
      
      {/* Value Amount */}
      <div className="mb-3 pb-3 border-b border-[#F3F4F6]">
        <p className="text-xs text-[#6B7280] mb-1">Scholarship Value</p>
        <p className="text-lg font-bold text-[#F97316]">
          {scholarship.value_amount ? (
            !isNaN(Number(scholarship.value_amount)) 
              ? `RM ${Number(scholarship.value_amount).toLocaleString()}`
              : scholarship.value_amount
          ) : (scholarship.coverage || 'Value varies')}
        </p>
      </div>
      
      {/* Details */}
      <div className="space-y-2 mb-4 text-xs">
        {scholarship.eligible_levels && scholarship.eligible_levels.length > 0 && (
          <div className="flex items-start gap-2 text-[#6B7280]">
            <svg className="w-3.5 h-3.5 text-[#1E40AF] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <span className="leading-tight">{scholarship.eligible_levels.join(', ')}</span>
          </div>
        )}
        
        <div className="flex items-start gap-2 text-[#6B7280]">
          <svg className="w-3.5 h-3.5 text-[#F97316] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="leading-tight">
            {scholarship.application_close ? new Date(scholarship.application_close).toLocaleDateString() : 
             (scholarship.availability === 'All Year' ? 'Open All Year' : 'All Year')}
          </span>
        </div>
      </div>
      
      {/* CTA Button */}
      <button className="w-full mt-auto px-4 py-2.5 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white font-semibold rounded-lg transition-all text-sm">
        View Details →
      </button>
    </div>
  );
};

export default ScholarshipListing;
